import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, prettifyApiError } from "@/shared/api";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { PageHeader } from "@/shared/ui/PageHeader";
import { RailLogo } from "@/shared/ui/RailLogo";

type Health = {
  status: string;
  version: string;
};

type CredentialKey =
  | "poleepo_token"
  | "imap_hostinger_password"
  | "thunderbird_path"
  | "poleepo_client_id"
  | "poleepo_client_secret";

type CredentialForm = Record<CredentialKey, string>;

type CredentialMeta = {
  key: string;
  label: string;
  keyring_name: string;
  created_at: string;
};

type OnboardingStatus = {
  complete: boolean;
  missing_credentials: string[];
};

type ThunderbirdProfile = {
  profile: string | null;
  mail_dir: string | null;
  candidates: string[];
};

type ValidateResult = {
  ok: boolean;
  detail?: string | null;
  info?: Record<string, unknown> | null;
};

const credentialLabels: Record<CredentialKey, string> = {
  poleepo_token: "Token Poleepo",
  poleepo_client_id: "Poleepo client_id (opzionale)",
  poleepo_client_secret: "Poleepo client_secret (opzionale)",
  imap_hostinger_password: "Password IMAP Hostinger",
  thunderbird_path: "Percorso profilo Thunderbird",
};

const initialForm: CredentialForm = {
  poleepo_token: "",
  poleepo_client_id: "",
  poleepo_client_secret: "",
  imap_hostinger_password: "",
  thunderbird_path: "",
};

// Required = bloccano "Avvia primo sync". Le opzionali sono utili ma non bloccanti.
const REQUIRED_KEYS: CredentialKey[] = [
  "poleepo_token",
  "imap_hostinger_password",
  "thunderbird_path",
];
const OPTIONAL_KEYS: CredentialKey[] = ["poleepo_client_id", "poleepo_client_secret"];

function StepBadge({ done, index }: { done: boolean; index: number }) {
  return (
    <span
      className={[
        "inline-flex h-6 w-6 items-center justify-center rounded-full border text-[12px] font-medium",
        done ? "border-green-soft bg-green-soft text-ink" : "border-line bg-card text-ink-3",
      ].join(" ")}
    >
      {done ? "OK" : index}
    </span>
  );
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const [healthOk, setHealthOk] = useState(false);
  const [credentialsSaved, setCredentialsSaved] = useState<Set<CredentialKey>>(new Set());
  const [form, setForm] = useState<CredentialForm>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // CHI sta lavorando, non SE: la pagina resta bloccata tutta (evita due save
  // in parallelo), ma il tasto premuto dice cosa sta facendo. Serve perche'
  // saveCredential fa due chiamate in fila (validate -> save, e per Poleepo il
  // validate esce davvero in rete): con un solo flag l'attesa era muta.
  const [busy, setBusy] = useState<null | "health" | CredentialKey | "complete">(null);
  const isBusy = busy !== null;

  useEffect(() => {
    // Stesso problema race-condition di StartRoute: se il backend ci mette
    // qualche secondo a partire, la prima loadInitialState fallisce, status
    // resta null, l'utente si trova davanti il wizard anche con onboarding
    // gia' completato in DB. Soluzione: polling fino a 30s.
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 15;
    const INTERVAL_MS = 2000;

    async function loadInitialState(): Promise<void> {
      try {
        const [health, credentials, status, thunderbird] = await Promise.all([
          apiGet<Health>("/health").catch(() => ({ status: "down", version: "" })),
          apiGet<CredentialMeta[]>("/api/credentials").catch(() => []),
          apiGet<OnboardingStatus>("/api/onboarding").catch(() => null),
          apiGet<ThunderbirdProfile>("/api/thunderbird/profile").catch(() => null),
        ]);
        if (cancelled) return;
        // Se non abbiamo ancora una risposta vera dal backend (status=null
        // perche' il backend non e' partito), riprova. Facciamo lo stesso
        // se l'health e' down: la pagina onboarding ha senso solo dopo
        // che il backend e' contattabile.
        const backendReady = status !== null && health.status === "ok";
        if (!backendReady) {
          attempts += 1;
          if (attempts < MAX_ATTEMPTS) {
            window.setTimeout(() => {
              if (!cancelled) void loadInitialState();
            }, INTERVAL_MS);
            return;
          }
          // Dopo 30s di tentativi: mostra il wizard cosi' Davide vede comunque
          // qualcosa (l'errore di rete in fondo basta).
        }
        setHealthOk(health.status === "ok");
        const savedKeys = new Set<CredentialKey>();
        for (const credential of credentials) {
          if (
            credential.key === "poleepo_token" ||
            credential.key === "imap_hostinger_password" ||
            credential.key === "thunderbird_path" ||
            credential.key === "poleepo_client_id" ||
            credential.key === "poleepo_client_secret"
          ) {
            savedKeys.add(credential.key);
          }
        }
        setCredentialsSaved(savedKeys);
        if (status?.complete) {
          void navigate("/ordini");
          return;
        }
        if (thunderbird?.mail_dir && !savedKeys.has("thunderbird_path")) {
          setForm((prev) => ({ ...prev, thunderbird_path: thunderbird.mail_dir ?? "" }));
        }
      } catch (err) {
        if (cancelled) return;
        // Audit 2026-05-29: prima schermata del nuovo utente -> errori
        // comprensibili in italiano via prettifyApiError (no stack/HTTP grezzi).
        setError(prettifyApiError(err));
      }
    }
    void loadInitialState();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function checkPostgres() {
    setBusy("health");
    try {
      const health = await apiGet<Health>("/health");
      setHealthOk(health.status === "ok");
      setError(null);
    } catch (err) {
      // Audit 2026-05-29: errore comprensibile via prettifyApiError.
      setError(prettifyApiError(err));
    } finally {
      setBusy(null);
    }
  }

  async function saveCredential(key: CredentialKey) {
    setBusy(key);
    try {
      const validate = await apiPost<ValidateResult>(`/api/credentials/${key}/validate`, {
        value: form[key],
      });
      if (!validate.ok) {
        // Audit 2026-05-29: il detail backend puo' essere tecnico -> passalo
        // per prettifyApiError; fallback italiano se assente.
        setError(
          validate.detail
            ? prettifyApiError(validate.detail)
            : `Validazione ${credentialLabels[key]} fallita`,
        );
        return;
      }
      await apiPost("/api/credentials", {
        key,
        label: credentialLabels[key],
        value: form[key],
      });
      setCredentialsSaved((prev) => new Set(prev).add(key));
      setError(null);
      setInfo(`${credentialLabels[key]} salvata.`);
    } catch (err) {
      // Audit 2026-05-29: errore comprensibile via prettifyApiError.
      setError(prettifyApiError(err));
    } finally {
      setBusy(null);
    }
  }

  async function completeOnboarding() {
    setBusy("complete");
    try {
      await apiPost("/api/onboarding/complete", { complete: true });
      setError(null);
      void navigate("/ordini");
    } catch (err) {
      // Audit 2026-05-29: errore comprensibile via prettifyApiError.
      setError(prettifyApiError(err));
    } finally {
      setBusy(null);
    }
  }

  const requiredSaved = REQUIRED_KEYS.every((key) => credentialsSaved.has(key));
  const canFinish = healthOk && requiredSaved;

  return (
    <main className="min-h-screen bg-paper text-ink">
      <div className="mx-auto flex min-h-screen max-w-[1120px] flex-col px-7 py-8">
        <div className="mb-8 flex items-center gap-3">
          <RailLogo size={46} />
          <div>
            <div className="font-display text-[24px] font-bold leading-tight">Order Manager</div>
            <div className="text-[13px] text-ink-3">Configurazione iniziale</div>
          </div>
        </div>

        <PageHeader
          title="Primo avvio"
          subtitle="Collega le credenziali locali prima di sincronizzare ordini reali."
          meta={
            <>
              Stato
              <br />
              <strong className="font-mono text-[11.5px] text-ink-2">
                {canFinish ? "pronto" : "da completare"}
              </strong>
            </>
          }
        />

        <ErrorBanner message={error} className="mb-4" />
        {info && !error && (
          <div className="mb-4 rounded-md border border-green-soft bg-green-soft px-3 py-2 text-[13px] text-ink">
            {info}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <Card>
              <div className="flex items-start gap-3">
                <StepBadge done={healthOk} index={1} />
                <div className="min-w-0 flex-1">
                  <h2 className="font-display text-[19px] font-bold">Postgres locale</h2>
                  <p className="mt-1 text-[13px] text-ink-3">
                    Verifica che backend e database rispondano prima di salvare credenziali.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void checkPostgres()}
                  disabled={isBusy}
                  aria-busy={busy === "health"}
                  className="rounded-md border border-line bg-card px-3 py-1.5 text-[12px] text-ink-2 hover:border-accent disabled:opacity-50"
                >
                  {busy === "health" ? "Verifico…" : "Verifica"}
                </button>
              </div>
            </Card>

            {REQUIRED_KEYS.map((key, idx) => (
              <Card key={key}>
                <div className="flex items-start gap-3">
                  <StepBadge done={credentialsSaved.has(key)} index={idx + 2} />
                  <div className="min-w-0 flex-1">
                    <label className="font-display text-[19px] font-bold" htmlFor={key}>
                      {credentialLabels[key]}
                    </label>
                    <input
                      id={key}
                      value={form[key]}
                      type={key.includes("password") || key.includes("token") ? "password" : "text"}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, [key]: event.target.value }))
                      }
                      className="mt-3 w-full rounded-md border border-line bg-card px-3 py-2 text-[13px] outline-none focus:border-accent"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => void saveCredential(key)}
                    disabled={isBusy || form[key].trim().length === 0}
                    aria-busy={busy === key}
                    className="rounded-md border border-line bg-card px-3 py-1.5 text-[12px] text-ink-2 hover:border-accent disabled:opacity-50"
                  >
                    {busy === key ? "Salvo…" : "Salva"}
                  </button>
                </div>
              </Card>
            ))}

            <details className="group">
              <summary className="cursor-pointer rounded-md border border-line bg-card px-4 py-3 text-[13px] font-medium text-ink-2 hover:border-accent">
                Opzioni avanzate Poleepo (auto-refresh token)
              </summary>
              <p className="mt-3 px-1 text-[12px] text-ink-3">
                Se hai client_id e client_secret API Poleepo, salvali per abilitare il rinnovo
                automatico del token. Senza queste credenziali devi rigenerare il token manualmente
                quando scade.
              </p>
              <div className="mt-3 space-y-3">
                {OPTIONAL_KEYS.map((key) => (
                  <Card key={key}>
                    <div className="flex items-start gap-3">
                      <StepBadge done={credentialsSaved.has(key)} index={0} />
                      <div className="min-w-0 flex-1">
                        <label className="font-display text-[16px] font-bold" htmlFor={key}>
                          {credentialLabels[key]}
                        </label>
                        <input
                          id={key}
                          value={form[key]}
                          type={key.includes("secret") ? "password" : "text"}
                          onChange={(event) =>
                            setForm((prev) => ({ ...prev, [key]: event.target.value }))
                          }
                          className="mt-3 w-full rounded-md border border-line bg-card px-3 py-2 text-[13px] outline-none focus:border-accent"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => void saveCredential(key)}
                        disabled={isBusy || form[key].trim().length === 0}
                        aria-busy={busy === key}
                        className="rounded-md border border-line bg-card px-3 py-1.5 text-[12px] text-ink-2 hover:border-accent disabled:opacity-50"
                      >
                        {busy === key ? "Salvo…" : "Salva"}
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            </details>
          </div>

          <Card title="Avanzamento" className="h-fit">
            <div className="space-y-3 text-[13px] text-ink-3">
              <p>
                Le credenziali stanno fuori dal database: keyring sul PC in sviluppo, variabili
                d’ambiente o secret file sul server.
              </p>
              <p>Il database conserva solo etichette e nomi dei riferimenti, mai i valori.</p>
              <button
                type="button"
                onClick={() => void completeOnboarding()}
                disabled={!canFinish || isBusy}
                aria-busy={busy === "complete"}
                className="mt-2 w-full rounded-md border border-accent bg-accent-2 px-3 py-2 text-[13px] font-medium text-white hover:brightness-95 disabled:border-line disabled:bg-paper-2 disabled:text-ink-4"
              >
                {busy === "complete" ? "Avvio…" : "Avvia primo sync"}
              </button>
            </div>
          </Card>
        </div>
      </div>
    </main>
  );
}
